/**
 * The router's error boundary — what renders when a route's loader or component
 * throws outright.
 *
 * This is NOT the API-failure path. A failed API call resolves with an error
 * envelope and goes to `showApiError()` → `ErrorModalHost`, which keeps the
 * screen a fan was on. Reaching here means the screen itself could not be
 * built, so there is nothing left to keep and the fan needs a way forward.
 */
import { Button } from "@sportsmet/ui";
import { useRouter } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { SUPPORT_EMAIL } from "../errors/ErrorModalHost";
import { HomeLink } from "./HomeLink";

export const RouteErrorScreen = ({ error }: { error: Error }): ReactNode => {
  const router = useRouter();

  return (
    <div
      role="alert"
      className="mx-auto flex max-w-[480px] flex-col items-center gap-4 py-12 text-center"
    >
      <h1 className="text-h1 text-ink">This screen didn&rsquo;t load</h1>
      <p className="text-body text-ink-secondary">
        Something went wrong on our end. Try again in a moment. Nothing you
        submitted was lost.
      </p>
      <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row-reverse">
        <Button
          variant="primary"
          onClick={() => {
            void router.invalidate();
          }}
        >
          Try again
        </Button>
        <Button variant="secondary" asChild>
          <HomeLink>Go to home</HomeLink>
        </Button>
      </div>
      <p className="text-caption text-ink-tertiary">
        Still stuck? Email {SUPPORT_EMAIL} and paste the line below.
      </p>
      {/*
        The raw message is developer detail, not fan copy — it is deliberately
        the smallest, quietest thing on the screen, and it is here only so a
        support reply has something concrete to work from.
      */}
      <pre className="max-w-full overflow-x-auto rounded-sm bg-surface-sunken p-3 text-left text-caption text-ink-tertiary">
        {error.message}
      </pre>
    </div>
  );
};
